import { AnyAction } from 'redux';
import { DocumentStatus } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';

import { ADD_OR_UPDATE_OFFER, RECOMMEND_OFFER, UPDATE_OFFER } from '../../common/actions/offer-actions';
import {
    DELETE_INQUIRY,
    DUPLICATE_INQUIRY,
    RECEIVE_INQUIRIES,
    RECEIVE_INQUIRY,
    REQUEST_INQUIRY,
    REQUEST_INQUIRY_END,
    REQUEST_INQUIRY_START,
    RESET_INQUIRIES_STATE,
    RESET_INQUIRY_STATE,
    UPDATE_INQUIRY_ALERT,
} from './inquiry-actions';
import { Inquiry } from './models/inquiry';
import { Offer } from '../../common/models/offer';

export interface MappedInquiries {
    [id: number]: Inquiry;
}

export interface InquiryReducerState {
    entity?: Inquiry;
    byId: MappedInquiries;
    flat: PaginatedResponse<Inquiry>;
    requesting: boolean;
}

const initialInquiryReducerState: InquiryReducerState = {
    entity: undefined,
    byId: {},
    flat: { limit: 10, page: 0, total: 0, items: [] },
    requesting: false,
};

export const mapInquiriesById = (inquiries: Inquiry[]): MappedInquiries => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return inquiries.reduce((accumulator: {}, inquiry) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accumulator as any)[inquiry.id] = inquiry;

        return accumulator;
    }, {});
};

const updateOffer = (offer: Offer, inquiry?: Inquiry) => {
    if (!inquiry) {
        return inquiry;
    }

    const purchases = [...inquiry.accountPurchases];
    let index = -1;
    const purchase = purchases.find(accountPurchase => {
        if (!accountPurchase.offers) {
            return false;
        }
        const existingOffer = accountPurchase.offers.find(searchOffer => searchOffer.id === offer.id);
        if (!existingOffer) {
            return false;
        }
        index = accountPurchase.offers.indexOf(existingOffer);
        return true;
    });

    if (purchase && purchase.offers && index >= 0) {
        purchase.offers[index] = offer;
        return { ...inquiry, accountPurchases: purchases };
    }
    return inquiry;
};

const addOrUpdateOffer = (accountPurchaseId: number, offer: Offer, inquiry?: Inquiry) => {
    if (!inquiry) {
        return inquiry;
    }

    const purchases = [...inquiry.accountPurchases];
    const accountPurchase = purchases.find(purchase => purchase.id === accountPurchaseId);
    if (!accountPurchase) {
        return inquiry;
    }

    const offers = (accountPurchase.offers && [...accountPurchase.offers]) || [];
    const foundOffer = offers.find(existingOffer => offer.id === existingOffer.id);
    if (foundOffer) {
        // Offer already exists, update
        const index = offers.indexOf(foundOffer);
        offers[index] = offer;
    } else {
        // New offer, just add to accountPurchase
        offers.push(offer);
    }
    accountPurchase.offers = offers;

    const status = inquiry.status !== DocumentStatus.Created ? inquiry.status : DocumentStatus.Ongoing;
    return { ...inquiry, status, accountPurchases: purchases };
};

const recommendOffer = (newRecommendedOfferId: number, isRecommended: boolean, inquiry?: Inquiry) => {
    if (!inquiry) {
        return inquiry;
    }

    // Search accountPurchase with recommended offer
    const purchases = [...inquiry.accountPurchases];
    const purchase = purchases.find(accountPurchase => {
        if (!accountPurchase.offers) {
            return false;
        }

        const foundOffer = accountPurchase.offers.find(offer => offer.id === newRecommendedOfferId);
        return !!foundOffer;
    });
    if (!purchase || !purchase.offers) {
        return inquiry;
    }

    // Update offer recommendations of found purchase
    const oldOffer = purchase.offers.find(offer => offer.isRecommended);
    const newOffer = purchase.offers.find(offer => offer.id === newRecommendedOfferId);
    if (!newOffer) {
        return inquiry;
    }
    if (oldOffer) {
        oldOffer.isRecommended = false;
    }
    newOffer.isRecommended = isRecommended;
    return { ...inquiry, accountPurchases: purchases };
};

export const inquiriesReducer = (
    state: InquiryReducerState = initialInquiryReducerState,
    action: AnyAction
): InquiryReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case RECEIVE_INQUIRIES: {
            return {
                ...state,
                byId: mapInquiriesById(action['inquiries'].items),
                flat: action['inquiries'],
            };
        }

        case RECEIVE_INQUIRY: {
            // Add or update current inquiry array
            const paginatedInquiries = { ...state.flat };
            const inquiries = [...paginatedInquiries.items];
            const existingInquiry = inquiries.find(inquiry => inquiry.id === action['entity'].id);
            if (existingInquiry) {
                const index = inquiries.indexOf(existingInquiry);
                inquiries[index] = action['entity'];
            } else {
                inquiries.unshift(action['entity']);
                // remove last one if surpassing page size
                if (inquiries.length > paginatedInquiries.limit) {
                    inquiries.pop();
                }
            }
            paginatedInquiries.items = inquiries;

            const entity = action['entity'] ?? state.entity;

            return {
                ...state,
                flat: paginatedInquiries,
                byId: mapInquiriesById(inquiries),
                entity,
                requesting: false,
            };
        }

        case RESET_INQUIRIES_STATE: {
            return {
                ...state,
                byId: initialInquiryReducerState.byId,
                flat: initialInquiryReducerState.flat,
            };
        }

        case RESET_INQUIRY_STATE: {
            return {
                ...state,
                entity: initialInquiryReducerState.entity,
            };
        }

        case DELETE_INQUIRY: {
            const paginatedInquiries = { ...state.flat };
            const inquiries = paginatedInquiries.items.filter(inquiry => inquiry.id !== rest['inquiryId']);
            paginatedInquiries.items = inquiries;
            paginatedInquiries.total--;

            return {
                ...state,
                flat: paginatedInquiries,
                byId: mapInquiriesById(inquiries),
                entity: initialInquiryReducerState.entity,
            };
        }

        case UPDATE_OFFER: {
            return {
                ...state,
                entity: updateOffer(rest['offer'], state.entity),
            };
        }

        case ADD_OR_UPDATE_OFFER: {
            return {
                ...state,
                entity: addOrUpdateOffer(rest['accountPurchaseId'], rest['offer'], state.entity),
            };
        }

        case RECOMMEND_OFFER: {
            return {
                ...state,
                entity: recommendOffer(rest['newRecommendedOfferId'], rest['isRecommended'], state.entity),
            };
        }

        case UPDATE_INQUIRY_ALERT: {
            if (!state.entity) {
                return state;
            }

            return {
                ...state,
                entity: {
                    ...state.entity,
                    receiptAlert: rest['alert'],
                },
            };
        }

        case REQUEST_INQUIRY:
        case DUPLICATE_INQUIRY:
        case REQUEST_INQUIRY_START: {
            return {
                ...state,
                requesting: true,
            };
        }

        case REQUEST_INQUIRY_END: {
            return {
                ...state,
                requesting: false,
            };
        }

        default: {
            return state;
        }
    }
};
